import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import {
  canStartRestore,
  installationName,
  jobLine,
  jobName,
  jobWarnings,
  restoreDefaults,
  sameInstallation,
  snapshotIso,
} from "@/lib/backupView";
import { tr, uiLocale } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

const BTN =
  "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)] disabled:opacity-40 disabled:cursor-not-allowed";
const NOTE = "text-xs px-3 py-2 border bg-amber-50 text-amber-800 border-amber-200";
const PILL = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial: "bg-amber-50 text-amber-800 border-amber-200",
  running: "bg-amber-50 text-amber-800 border-amber-200",
  error: "bg-red-50 text-red-700 border-red-200",
};

const when = (iso) => {
  const d = new Date(iso);
  return iso && !Number.isNaN(d.getTime()) ? d.toLocaleString(uiLocale()) : "";
};

/**
 * Admin → Restore from backup.
 *
 * Lists everything in the backup bucket (one row per property, one per
 * installation's accounts) and starts verify / restore jobs. Lives on
 * the Admin page because it has to work on a rebuilt server, before any
 * property exists for a Settings page to belong to.
 *
 * One job runs at a time; the list at the top polls it.
 */
export default function BackupRestorePanel() {
  const qc = useQueryClient();
  const { data: bucket, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "backup-bucket"],
    queryFn: api.adminBackupBucket,
    staleTime: 30_000,
  });
  const { data: jobData } = useQuery({
    queryKey: ["admin", "backup-jobs"],
    queryFn: api.adminBackupJobs,
    refetchInterval: (q) => (q.state.data?.running ? 3000 : false),
  });
  const jobs = jobData?.jobs || [];
  const busy = !!jobData?.running;

  // When a job finishes, what is "here" may have changed.
  useEffect(() => {
    if (!busy) {
      qc.invalidateQueries({ queryKey: ["admin", "backup-bucket"] });
      qc.invalidateQueries({ queryKey: ["admin", "properties"] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    }
  }, [busy, qc]);

  const start = useMutation({
    mutationFn: (payload) => api.adminStartBackupJob(payload),
    onSuccess: () => {
      toast.success(tr("Started"));
      qc.invalidateQueries({ queryKey: ["admin", "backup-jobs"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Couldn't start that")),
  });
  const blocked = busy || start.isPending;

  // { type: "property" | "accounts", scope }
  const [open, setOpen] = useState(null);
  const toggle = (type, scope) =>
    setOpen((cur) =>
      cur && cur.type === type && cur.scope === scope ? null : { type, scope }
    );

  const currentId = bucket?.installation?.id || "";
  const properties = bucket?.properties || [];
  const accounts = bucket?.accounts || [];

  return (
    <section className="mt-10 mb-12" data-testid="backup-restore-panel">
      <div className="flex items-center gap-2 mb-3">
        <Archive className="w-4 h-4 text-[var(--accent)]" />
        <div className="font-serif-display text-xl">{tr("Restore from backup")}</div>
      </div>

      <p className="text-xs text-[var(--ink-muted)] leading-relaxed max-w-3xl mb-4">{tr("Everything in the backup bucket, from every installation that writes to it. “Check” downloads a backup and proves every file is intact without changing anything. Restoring a property that exists here replaces its data, after first backing up what is there now. Restoring one that does not exist here creates it.")}</p>

      {isLoading && (
        <div className="text-sm text-[var(--ink-muted)] flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}{tr("Reading the bucket…")}</div>
      )}
      {error && (
        <div className="text-xs px-3 py-2 border bg-red-50 text-red-700 border-red-200">
          {error?.response?.data?.detail || tr("Could not read the backup bucket.")}{" "}
          <button type="button" onClick={() => refetch()} className="underline">{tr("Try again")}</button>
        </div>
      )}
      {bucket && !bucket.configured && (
        <div className={NOTE}>
          {bucket.problem ||
            tr("Backups are not set up on this installation. Missing on the server: {list}.", { list: (bucket.missing || []).join(", ") })}
        </div>
      )}

      {jobs.length > 0 && (
        <ul
          data-testid="backup-jobs"
          className="mb-5 divide-y divide-[var(--border)] border border-[var(--border)] bg-white"
        >
          {jobs.map((j) => (
            <li key={j.id} className="px-3 py-2 text-xs space-y-1">
              <div className="flex flex-wrap items-center justify-between gap-3">
                {trRich("<0><1>{jobName}</1><2> · {when} · backup of {when2}</2></0>", { jobName: jobName(j), when: when(j.started_at), when2: when(snapshotIso(j.snapshot_id)) }, [<span />, <span className="font-medium" />, <span className="text-[var(--ink-muted)]" />])}
                <span
                  className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] uppercase tracking-wider font-mono-display ${
                    PILL[j.status] || PILL.running
                  }`}
                >
                  {j.status === "running" && (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  )}
                  {j.status}
                </span>
              </div>
              <div className={j.status === "error" ? "text-red-700" : ""}>
                {jobLine(j)}
              </div>
              {jobWarnings(j).map((w) => (
                <div key={w} className={NOTE}>
                  {w}
                </div>
              ))}
              {j.kind === "restore_property" && j.status !== "running" && j.summary?.created && (
                <div className="text-[var(--ink-muted)]">{tr("Reload the page to see it in the property switcher.")}</div>
              )}
              {j.summary?.safety_snapshot_id && (
                <div className="text-[var(--ink-muted)]">{tr("The data from before this restore was saved as the backup of {when}.", { when: when(snapshotIso(j.summary.safety_snapshot_id)) })}</div>
              )}
            </li>
          ))}
        </ul>
      )}

      {bucket?.configured && (
        <>
          <div className="overline mb-2">{tr("Properties")}</div>
          {properties.length === 0 ? (
            <div className="text-xs text-[var(--ink-muted)] italic mb-5">{tr("No property backups in the bucket yet.")}</div>
          ) : (
            <ul className="mb-6 divide-y divide-[var(--border)] border border-[var(--border)] bg-white">
              {properties.map((row) => (
                <li key={row.scope} data-testid={`backup-row-${row.scope}`}>
                  <RowHead
                    title={row.name || row.scope}
                    sub={
                      row.legacy
                        ? tr("{value}older backup, from before installations were kept apart", { value: row.slug ? `${row.slug} · ` : "" })
                        : `${row.slug ? `${row.slug} · ` : ""}from ${installationName(row.installation, currentId)}`
                    }
                    row={row}
                    here={
                      row.exists_here
                        ? tr("On this installation")
                        : tr("Not on this installation")
                    }
                    blocked={blocked}
                    onCheck={() =>
                      start.mutate({ kind: "verify", scope: row.scope, snapshot_id: row.snapshot_id })
                    }
                    onRestore={() => toggle("property", row.scope)}
                    isOpen={open?.type === "property" && open.scope === row.scope}
                  />
                  {open?.type === "property" && open.scope === row.scope && (
                    <PropertyRestoreForm
                      row={row}
                      currentId={currentId}
                      blocked={blocked}
                      onStart={(payload) =>
                        start.mutate(payload, { onSuccess: () => setOpen(null) })
                      }
                    />
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="overline mb-2">{tr("Accounts and shared settings")}</div>
          {accounts.length === 0 ? (
            <div className="text-xs text-[var(--ink-muted)] italic">{tr("No accounts backups in the bucket yet.")}</div>
          ) : (
            <ul className="divide-y divide-[var(--border)] border border-[var(--border)] bg-white">
              {accounts.map((row) => (
                <li key={row.scope} data-testid={`backup-accounts-${row.install_id || "legacy"}`}>
                  <RowHead
                    title={
                      row.legacy
                        ? tr("Older backups (before installations were kept apart)")
                        : installationName(
                            { ...row.installation, id: row.install_id },
                            currentId
                          )
                    }
                    sub={
                      row.legacy
                        ? tr("May mix the live site and the preview")
                        : `id ${row.install_id}`
                    }
                    row={row}
                    here=""
                    blocked={blocked}
                    onCheck={() =>
                      start.mutate({ kind: "verify", scope: row.scope, snapshot_id: row.snapshot_id })
                    }
                    onRestore={() => toggle("accounts", row.scope)}
                    isOpen={open?.type === "accounts" && open.scope === row.scope}
                  />
                  {open?.type === "accounts" && open.scope === row.scope && (
                    <AccountsRestoreForm
                      row={row}
                      blocked={blocked}
                      onStart={(payload) =>
                        start.mutate(payload, { onSuccess: () => setOpen(null) })
                      }
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function RowHead({ title, sub, row, here, blocked, onCheck, onRestore, isOpen }) {
  return (
    <div className="px-3 py-2.5 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{title}</div>
        <div className="text-[11px] text-[var(--ink-muted)] font-mono-display">
          {sub}
        </div>
        <div className="text-[11px] text-[var(--ink-muted)]">{tr("Last backup {when} · {value} records{value2}", { when: when(row.created_at), value: Number(row.records || 0).toLocaleString("en-US"), value2: here ? ` · ${here}` : "" })}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={onCheck} disabled={blocked} className={BTN}>
          <ShieldCheck className="w-3.5 h-3.5" />{" "}{tr("Check")}</button>
        <button type="button" onClick={onRestore} disabled={blocked && !isOpen} className={BTN}>
          {isOpen ? tr("Close") : tr("Restore…")}
        </button>
      </div>
    </div>
  );
}

function SnapshotPicker({ scope, value, onChange }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "backup-snapshots", scope],
    queryFn: () => api.adminBackupSnapshots(scope),
  });
  const snaps = data?.snapshots || [];
  useEffect(() => {
    if (!value && snaps.length > 0) onChange(snaps[0].id);
  }, [value, snaps, onChange]);
  return (
    <label className="block text-xs">
      {trRich("<0>Backup to restore</0>", null, [<span className="block mb-1 text-[var(--ink-muted)]" />])}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={isLoading || snaps.length === 0}
        className="border border-[var(--border)] px-2 py-1.5 text-sm bg-white min-w-[16rem]"
      >
        {isLoading && <option value="">{tr("Loading…")}</option>}
        {!isLoading && snaps.length === 0 && (
          <option value="">{tr("No finished backups")}</option>
        )}
        {snaps.map((s, i) => (
          <option key={s.id} value={s.id}>
            {when(s.created_at)}
            {i === 0 ? tr(" (latest)") : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

function Check({ checked, onChange, children, testId }) {
  return (
    <label className="flex items-start gap-2 text-sm cursor-pointer">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 mt-0.5 accent-[var(--accent)]"
      />
      <span>{children}</span>
    </label>
  );
}

function PropertyRestoreForm({ row, currentId, blocked, onStart }) {
  const [snapshotId, setSnapshotId] = useState("");
  const [opts, setOpts] = useState(() => restoreDefaults(row, currentId));
  const [typed, setTyped] = useState("");
  const same = sameInstallation(row, currentId);
  const ready = canStartRestore({ row, snapshotId, typed, busy: blocked });

  return (
    <div className="px-3 pb-4 pt-1 space-y-3 bg-[var(--bg)] border-t border-[var(--border)]">
      <SnapshotPicker scope={row.scope} value={snapshotId} onChange={setSnapshotId} />

      <div className="space-y-2">
        <Check
          testId="restore-connections"
          checked={opts.restore_connections}
          onChange={(v) => setOpts({ ...opts, restore_connections: v })}
        >{tr("Restore the connections too (WordPress, Facebook, MailerLite, PublishOS)")}</Check>
        {!same && opts.restore_connections && (
          <div className={NOTE}>{tr("This backup came from a different installation. With the connections restored, this installation will hold the same live credentials and could post to the same Pages and site. Only do this when it is replacing that installation.")}</div>
        )}
        {!opts.restore_connections && (
          <p className="text-[11px] text-[var(--ink-muted)] pl-6">{tr("Left blank, the property is restored without any credentials and you reconnect each service in Settings.")}</p>
        )}
        <Check
          testId="restore-schedules"
          checked={opts.keep_schedules}
          onChange={(v) => setOpts({ ...opts, keep_schedules: v })}
        >{tr("Keep future scheduled posts and research schedules switched on")}</Check>
        <p className="text-[11px] text-[var(--ink-muted)] pl-6">{tr("Off: scheduled posts come back cancelled and research schedules come back disabled, so nothing fires by itself. Posts whose time has already passed are always cancelled.")}</p>
        <Check
          testId="restore-history"
          checked={opts.include_history}
          onChange={(v) => setOpts({ ...opts, include_history: v })}
        >{tr("Include history (AI cost records and prompt history)")}</Check>
      </div>

      {row.exists_here ? (
        <div className="space-y-2">
          <div className="text-xs px-3 py-2 border bg-red-50 text-red-700 border-red-200">{tr("“{name_here}” exists on this installation. Its articles, research, newsletters, ads and settings will be replaced with the backup. What is there now is backed up first.", { name_here: row.name_here })}</div>
          <label className="block text-xs">
            {trRich("<0>Type the property’s name to confirm</0>", null, [<span className="block mb-1 text-[var(--ink-muted)]" />])}
            <input
              data-testid="restore-confirm-name"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={row.name_here}
              className="border border-[var(--border)] px-2 py-1.5 text-sm bg-white w-72 max-w-full outline-none focus:border-[var(--slate-900)]"
            />
          </label>
        </div>
      ) : (
        <p className="text-xs text-[var(--ink-muted)]">{tr("This property is not on this installation, so it will be created and you will be given access to it.")}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="restore-start"
          disabled={!ready}
          onClick={() =>
            onStart({
              kind: "restore_property",
              scope: row.scope,
              snapshot_id: snapshotId,
              confirm: typed,
              ...opts,
            })
          }
          className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {row.exists_here ? tr("Replace with this backup") : tr("Restore this property")}
        </button>
        {row.exists_here && (
          <button
            type="button"
            disabled={blocked || !snapshotId}
            onClick={() =>
              onStart({ kind: "restore_images", scope: row.scope, snapshot_id: snapshotId })
            }
            title={tr("Put back any image that is missing from storage. Changes no records.")}
            className={BTN}
          >{tr("Copy images again")}</button>
        )}
      </div>
    </div>
  );
}

function AccountsRestoreForm({ row, blocked, onStart }) {
  const [snapshotId, setSnapshotId] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  return (
    <div className="px-3 pb-4 pt-1 space-y-3 bg-[var(--bg)] border-t border-[var(--border)]">
      <SnapshotPicker scope={row.scope} value={snapshotId} onChange={setSnapshotId} />
      <p className="text-xs text-[var(--ink-muted)] max-w-2xl">{tr("Adds every account in the backup that this installation does not have, with its password and property access. An account that already exists here is never changed.")}</p>
      <Check checked={overwrite} onChange={setOverwrite} testId="restore-overwrite-settings">{tr("Also overwrite shared settings that already exist here (AI model choices and other installation-wide settings)")}</Check>
      <button
        type="button"
        data-testid="restore-accounts-start"
        disabled={blocked || !snapshotId}
        onClick={() =>
          onStart({
            kind: "restore_accounts",
            scope: row.scope,
            snapshot_id: snapshotId,
            overwrite_settings: overwrite,
          })
        }
        className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
      >{tr("Restore accounts")}</button>
    </div>
  );
}
